import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, CalendarIcon, ClockIcon, BanknoteIcon, PencilIcon, Trash2Icon, CheckSquareIcon } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
interface ManageTasksPageProps {
  tasks: Task[];
  customers: Customer[];
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
}
type StatusFilter = 'all' | 'planned' | 'in-progress' | 'pending' | 'todo' | 'done';
type CategoryFilter = 'all' | 'Ladeboks' | 'Andet';
export function ManageTasksPage({
  tasks,
  customers,
  onEditTask,
  onDeleteTask
}: ManageTasksPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch = !query || task.title.toLowerCase().includes(query) || task.taskId?.toLowerCase().includes(query) || task.customerName?.toLowerCase().includes(query);
      // Status filter
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      // Category filter
      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    }).sort((a, b) => {
      // Sort by newest first (using taskId as proxy for creation order)
      const aNum = parseInt(a.taskId?.substring(1) || '0');
      const bNum = parseInt(b.taskId?.substring(1) || '0');
      return bNum - aNum;
    });
  }, [tasks, searchQuery, statusFilter, categoryFilter]);
  const statusConfig: Record<string, {
    label: string;
    color: string;
    bgColor: string;
    dotColor: string;
  }> = {
    planned: {
      label: 'Planlagt',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      dotColor: 'bg-blue-500'
    },
    pending: {
      label: 'Afventende',
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
      dotColor: 'bg-purple-500'
    },
    'in-progress': {
      label: 'I gang',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      dotColor: 'bg-amber-500'
    },
    todo: {
      label: 'Todo',
      color: 'text-stone-700',
      bgColor: 'bg-stone-100',
      dotColor: 'bg-stone-500'
    },
    done: {
      label: 'Afsluttet',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      dotColor: 'bg-emerald-500'
    }
  };
  const statusFilters: {
    value: StatusFilter;
    label: string;
  }[] = [{
    value: 'all',
    label: 'Alle'
  }, {
    value: 'planned',
    label: 'Planlagt'
  }, {
    value: 'in-progress',
    label: 'I gang'
  }, {
    value: 'pending',
    label: 'Afventende'
  }, {
    value: 'todo',
    label: 'Todo'
  }, {
    value: 'done',
    label: 'Afsluttet'
  }];
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="min-h-screen bg-stone-50 py-8" data-id="element-1594" data-mp="30git00a5cp9i">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1595" data-mp="2mhjdpj8ixmq0">
        {/* Header Section */}
        <div className="mb-8" data-id="element-1596" data-mp="2rg44yt5ssw2b">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1597" data-mp="2rfb9iocahszh">
            Ændre opgaver
          </h1>
          <p className="text-stone-500 mt-2" data-id="element-1598" data-mp="1b0jes4ro89n0">
            Administrer og rediger alle opgaver i systemet
          </p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-8 shadow-sm" data-id="element-1599" data-mp="1vb426tkoswmn">
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-1600" data-mp="1qjm0226xgq03">
            {/* Search */}
            <div className="flex-1" data-id="element-1601" data-mp="1miaj0l24dd3i">
              <div className="relative" data-id="element-1602" data-mp="1mjbw0v2p49nv">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1603" data-mp="1mg64782omtog" />
                <input type="text" placeholder="Søg efter opgave, kunde eller ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all" data-id="element-1604" data-mp="2e9q3a8g7enqk" />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2" data-id="element-1605" data-mp="otqoaapwm430">
              <FilterIcon className="w-4 h-4 text-stone-400" data-id="element-1606" data-mp="ox4phfmu078v" />
              <div className="flex p-1 bg-stone-50 rounded-lg border border-stone-200" data-id="element-1607" data-mp="esell47vat20">
                {(['all', 'Ladeboks', 'Andet'] as const).map(cat => <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${categoryFilter === cat ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-1608" data-mp="oohm65n2mjmu">
                    {cat === 'all' ? 'Alle' : cat === 'Andet' ? 'Alm. Inst.' : cat}
                  </button>)}
              </div>
            </div>
          </div>

          {/* Status Chips */}
          <div className="flex flex-wrap items-center gap-2 mt-6 pt-6 border-t border-stone-100" data-id="element-1609" data-mp="3e5p174hz41zg">
            <span className="text-sm font-medium text-stone-500 mr-2" data-id="element-1610" data-mp="3650wgxa1uo18">
              Status:
            </span>
            {statusFilters.map(filter => <button key={filter.value} onClick={() => setStatusFilter(filter.value)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${statusFilter === filter.value ? 'bg-stone-800 text-white border-stone-800 shadow-sm' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50'}`} data-id="element-1611" data-mp="3180rkpoxrrbp">
                {filter.label}
              </button>)}
          </div>
        </div>

        {/* Task List */}
        <div className="space-y-3" data-id="element-1612" data-mp="3tuv85bimu6jw">
          <AnimatePresence mode="popLayout" data-id="element-1613" data-mp="3tvwkrkfar13q">
            {filteredTasks.length === 0 ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-2xl border border-stone-200 border-dashed p-12 flex flex-col items-center justify-center text-center" data-id="element-1614" data-mp="bogxpmjkoeq9">
                <div className="w-16 h-16 bg-stone-50 rounded-full flex items-center justify-center mb-4" data-id="element-1615" data-mp="m9uc9w5wauus">
                  <CheckSquareIcon className="w-8 h-8 text-stone-300" data-id="element-1616" data-mp="7l7mf0in6kpb" />
                </div>
                <h3 className="text-lg font-semibold text-stone-900 mb-1" data-id="element-1617" data-mp="ccpqvyjvj3f1">
                  Ingen opgaver fundet
                </h3>
                <p className="text-stone-500 max-w-sm" data-id="element-1618" data-mp="3urqh8qlqdreg">
                  Vi kunne ikke finde nogen opgaver der matcher dine nuværende
                  filtre og søgekriterier.
                </p>
                {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && <button onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setCategoryFilter('all');
            }} className="mt-6 text-teal-600 font-medium hover:text-teal-700 transition-colors" data-id="element-1619" data-mp="3pt5jcxtybjt6">
                    Nulstil filtre
                  </button>}
              </motion.div> : filteredTasks.map((task, index) => {
            const status = statusConfig[task.status] || statusConfig.todo;
            return <motion.div layout initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              duration: 0.2,
              delay: index * 0.03
            }} key={task.id} className="group bg-white rounded-2xl border border-stone-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all" data-id="element-1620" data-mp="3ck59cb9n6ry5" data-mpi={index}>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6" data-id="element-1621" data-mp="1s12vda2dj878">
                      {/* Left Column: Status & Title */}
                      <div className="flex-1 min-w-0" data-id="element-1622" data-mp="10b7seallfk03">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1623" data-mp="10bzqbs2nxttj">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${status.bgColor} ${status.color}`} data-id="element-1624" data-mp="14cjliq3oend1">
                            <div className={`w-1.5 h-1.5 rounded-full ${status.dotColor}`} data-id="element-1625" data-mp="ckkijjs4akaw" />
                            {status.label}
                          </div>
                          <span className="text-xs font-medium text-stone-400 bg-stone-100 px-2 py-1 rounded-md" data-id="element-1626" data-mp="4nr68rfcmn9s">
                            {task.taskId}
                          </span>
                          <span className="text-xs font-medium text-stone-500" data-id="element-1627" data-mp="dq8v7dme1u0h">
                            {task.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-stone-900 truncate" data-id="element-1628" data-mp="3i471tzkq3js5">
                          {task.title}
                        </h3>
                        <p className="text-sm text-stone-500 mt-1 truncate" data-id="element-1629" data-mp="3m2egmovmmb0p">
                          {task.customerName || 'Ingen kunde'}
                        </p>
                      </div>

                      {/* Middle Column: Dates */}
                      <div className="flex-1 flex flex-col gap-2 min-w-[200px]" data-id="element-1630" data-mp="2ye52880qspjl">
                        {task.agreedPlanning && <div className="flex items-center gap-2 text-sm text-stone-600" data-id="element-1631" data-mp="33cg7s6k08dw0">
                            <CalendarIcon className="w-4 h-4 text-stone-400" data-id="element-1632" data-mp="2vtyya0c4oz31" />
                            <span className="font-medium text-stone-700" data-id="element-1633" data-mp="2qwyr1m3jlqac">
                              Proj:
                            </span>
                            {formatDateTime(task.agreedPlanning)}
                          </div>}
                        {task.agreedInstallation && <div className="flex items-center gap-2 text-sm text-stone-600" data-id="element-1634" data-mp="2cx77z6y65jph">
                            <CalendarIcon className="w-4 h-4 text-stone-400" data-id="element-1635" data-mp="283kzzs2ya400" />
                            <span className="font-medium text-stone-700" data-id="element-1636" data-mp="2374cn03krtnz">
                              Inst:
                            </span>
                            {formatDateTime(task.agreedInstallation)}
                          </div>}
                        {!task.agreedPlanning && !task.agreedInstallation && <span className="text-sm text-stone-400 italic" data-id="element-1637" data-mp="1fipfhaoullbd">
                            Ingen datoer planlagt
                          </span>}
                      </div>

                      {/* Right Column: Price & Hours */}
                      <div className="flex-1 flex flex-col items-start lg:items-end gap-2" data-id="element-1638" data-mp="15mcmkf14nbho">
                        <div className="flex items-center gap-2 text-sm" data-id="element-1639" data-mp="1ak4o80rkqbz2">
                          <BanknoteIcon className="w-4 h-4 text-stone-400" data-id="element-1640" data-mp="1ya8hbe74ytbt" />
                          <span className="font-semibold text-stone-900" data-id="element-1641" data-mp="2380hevhxplr3">
                            {(Number(task.totalPrice) || 0).toLocaleString('da-DK')}{' '}
                            kr
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm" data-id="element-1642" data-mp="2h7rw6yg9z665">
                          <ClockIcon className="w-4 h-4 text-stone-400" data-id="element-1643" data-mp="2lyhz8518awxo" />
                          <span className="text-stone-600" data-id="element-1644" data-mp="2m0uluvhxlvh2">
                            {task.hours || 0} timer
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-4 lg:pt-0 border-t lg:border-t-0 border-stone-100 lg:pl-4 lg:border-l" data-id="element-1645" data-mp="jcclddr14dpi">
                        <button onClick={() => onEditTask(task)} className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-stone-50 hover:bg-teal-50 text-stone-700 hover:text-teal-700 rounded-xl font-medium transition-colors border border-stone-200 hover:border-teal-200" data-id="element-1646" data-mp="fb11zpyr0opr">
                          <PencilIcon className="w-4 h-4" data-id="element-1647" data-mp="3rquwjoc9o9dc" />
                          <span className="lg:hidden" data-id="element-1648" data-mp="1c0rx9rev6fwm">Rediger</span>
                        </button>
                        <button onClick={() => onDeleteTask(task)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-200" title="Slet opgave" data-id="element-1649" data-mp="171p16wgv3k0s">
                          <Trash2Icon className="w-4 h-4" data-id="element-1650" data-mp="2c9016p5e8pe7" />
                        </button>
                      </div>
                    </div>
                  </motion.div>;
          })}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}